import { AgCheckBoxCellRendererComponent } from 'components/AgGrid/AgCheckBoxCellRendererComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { DismantlingCoefficientTypeEnum, PriceListUerDismantlingRatioData } from 'gql/graphql';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import {
    uerPriceListUerForDismantlingRatioUerCodeFilter,
    uerPriceListUerForDismantlingRatioUerGroupFilter,
    uerPriceListUerForDismantlingRatioUerNameFilter,
    uerPriceListUerForDismantlingRatioUerUnitFilter,
} from '../components/UerSelectionModalQueries';

interface IUseUerPriceListUerForDismantlingRatioColumns {
    onCheckChange?: (checkedIds: number[]) => void;
    checkedIds?: number[];
    isReadOnly: boolean;
    setFilterValues: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
    groupType: DismantlingCoefficientTypeEnum | null;
    contractId: number;
    uerId: number;
}

export const useUerPriceListUerForDismantlingRatioColumns = (
    props: IUseUerPriceListUerForDismantlingRatioColumns,
): ColDef[] => {
    const { checkedIds, onCheckChange, isReadOnly, setFilterValues, groupType, contractId, uerId } = props;

    const filterVariables = useMemo(
        () => ({
            groupType,
            contractId,
            preselectedUerBaseRowIds: [uerId],
        }),
        [contractId, groupType, uerId],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue: any) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [setFilterValues],
    );

    return useMemo(
        () => [
            ...(isReadOnly
                ? []
                : [
                      {
                          field: 'checkbox' as any,
                          headerName: '',
                          width: 55,
                          cellRenderer: AgCheckBoxCellRendererComponent,
                          cellRendererParams: {
                              checkedIds,
                              onCheckChange,
                              idKey: 'uerId',
                          },
                      },
                  ]),
            {
                headerName: 'Подраздел',
                field: 'groupName',
                width: isReadOnly ? 470 : 450,
                valueGetter: ({ data }: { data: PriceListUerDismantlingRatioData }) =>
                    data ? `${data?.uerGroupCode} ${data?.uerGroupName}` : '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerForDismantlingRatioUerGroupFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    variables: filterVariables,
                    query: uerPriceListUerForDismantlingRatioUerGroupFilter,
                },
            },
            {
                headerName: 'Шифр',
                field: 'uerCode',
                width: 160,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerForDismantlingRatioUerCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: string | null }) => value ?? 'Без шифра',
                        },
                    ],
                    variables: filterVariables,
                    query: uerPriceListUerForDismantlingRatioUerCodeFilter,
                },
            },
            {
                headerName: 'Наименование',
                field: 'uerName',
                width: isReadOnly ? 470 : 450,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerForDismantlingRatioUerNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    variables: filterVariables,
                    query: uerPriceListUerForDismantlingRatioUerNameFilter,
                },
            },
            {
                headerName: 'Ед. изм.',
                field: 'unit',
                width: 120,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerForDismantlingRatioUerUnitFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    variables: filterVariables,
                    query: uerPriceListUerForDismantlingRatioUerUnitFilter,
                },
            },
        ],
        [changeFilterHandler, checkedIds, filterVariables, isReadOnly, onCheckChange],
    );
};
